import { Typography } from "@/components/atoms";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useGuestLabels } from "@/modules/guest-web/hooks/useGuestLabels";
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";

const BUTTON_WIDTH = 145;
const BUTTON_HEIGHT = 45;

export interface GuestFloatingLoginButtonFixedProps {
  readonly onPress: () => void;
}

/**
 * Fixed 145x45 floating Login CTA for guest mobile screens (per design
 * spec) — the shared GuestFloatingLoginButton (guest-web, used by web too)
 * hugs its label / goes full-width on phone instead, so this is a small
 * standalone mobile-only variant rather than changing that shared sizing.
 */
export function GuestFloatingLoginButtonFixed({
  onPress,
}: Readonly<GuestFloatingLoginButtonFixedProps>) {
  const colors = useUIColors();
  const labels = useGuestLabels();
  const { isRTL } = useI18nContext();

  return (
    <View style={[styles.wrap, isRTL ? styles.wrapLeft : styles.wrapRight]} pointerEvents="box-none">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={labels.login}
        onPress={onPress}
        style={({ pressed }) => [
          styles.button,
          {
            backgroundColor: colors.buttonDefault,
            shadowColor: colors.fontPrimary,
            opacity: pressed ? 0.92 : 1,
          },
        ]}
      >
        <Typography variant="headline" color="white" numberOfLines={1} style={styles.label}>
          {labels.login}
        </Typography>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: "absolute",
    bottom: 70,
    zIndex: 20,
  },
  wrapRight: {
    right: 16,
  },
  wrapLeft: {
    left: 16,
  },
  button: {
    width: BUTTON_WIDTH,
    height: BUTTON_HEIGHT,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: BUTTON_HEIGHT / 2,
    shadowOpacity: 0.62,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 12,
  },
  label: {
    letterSpacing: 0,
    textAlign: "center",
  },
});

export default GuestFloatingLoginButtonFixed;
